/**
 * TimelineBar — Selector de rango temporal con presets + noUiSlider
 *
 * Usa noUiSlider (MIT, ~9KB) para el slider horizontal con handles arrastrables.
 * Los presets (Hoy, 7D, 15D, 1M, 3M, AÑO) mueven el slider programáticamente.
 *
 * Instanciable: permite múltiples timelines simultáneos en la misma página.
 *
 * Uso como clase:
 *   const tl = new TimelineBarInstance(container, (desde, hasta, preset) => { ... });
 *   tl.destroy();
 *
 * Uso estático (compatibilidad):
 *   TimelineBar.init(container, onChange);
 */

import noUiSlider from '../lib/nouislider.js';
import { t } from '../services/i18n-admin.js';

// El último preset es el AÑO en curso (01/ene → hoy). Reemplaza al viejo "Todo"
// que emitía desde/hasta nulos y el backend malinterpretaba como "solo hoy".
// dias: -1 = marca especial "año en curso"; el texto del botón es el año vigente.
var PRESETS = [
    { label: 'Hoy', dias: 0 },
    { label: '7D', dias: 7 },
    { label: '15D', dias: 15 },
    { label: '1M', dias: 30 },
    { label: '3M', dias: 90 },
    { label: 'Anio', dias: -1 },
];

var PRESETS_MAP = {};
for (var p = 0; p < PRESETS.length; p++) {
    PRESETS_MAP[PRESETS[p].label] = PRESETS[p];
}

var MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
var DEBOUNCE_MS = 400;
var _uidCounter = 0;

// ============================================================================
// HELPERS (sin estado, compartidos)
// ============================================================================

function fmtISO(d) {
    if (!d) return null;
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function fmtDisplay(d) {
    if (!d) return '';
    return String(d.getDate()).padStart(2, '0') + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + d.getFullYear();
}

function fmtHora(d) {
    if (!d) return '';
    return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

function getFinDelDia() {
    var d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
}

function getInicioDelDia() {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function getInicioDelAnio() {
    var d = new Date();
    d.setMonth(0);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
}

function getTimezoneLabel() {
    try {
        var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        var city = tz.split('/').pop().replace(/_/g, ' ');
        var offset = -new Date().getTimezoneOffset();
        var sign = offset >= 0 ? '+' : '-';
        var hrs = Math.floor(Math.abs(offset) / 60);
        return city + ' UTC' + sign + hrs;
    } catch (_) {
        return '';
    }
}

function inyectarEstilosNoUiSlider() {
    if (document.getElementById('tl-nouislider-theme')) return;
    var style = document.createElement('style');
    style.id = 'tl-nouislider-theme';
    style.textContent = [
        // Base del slider — más alto
        '.tl-slider-wrap .noUi-target{height:10px;border:none;background:var(--border-glass,#1e3654);border-radius:5px;box-shadow:inset 0 1px 2px rgba(0,0,0,0.3)}',
        '.tl-slider-wrap .noUi-connect{background:#3b82f6;border-radius:5px}',
        '.tl-slider-wrap .noUi-handle{width:8px;height:18px;top:-4px;right:-4px;border-radius:2px;background:#3b82f6;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);cursor:grab}',
        '.tl-slider-wrap .noUi-handle:active{cursor:grabbing;background:#2563eb}',
        '.tl-slider-wrap .noUi-handle::before,.tl-slider-wrap .noUi-handle::after{display:none}',
    ].join('\n');
    document.head.appendChild(style);
}

// ============================================================================
// CLASE INSTANCIABLE
// ============================================================================

class TimelineBarInstance {
    /**
     * @param {HTMLElement} container
     * @param {Function} onChange — (desde, hasta, preset) => void
     * @param {Object} [opciones]
     * @param {string} [opciones.presetInicial='1M']
     */
    constructor(container, onChange, opciones) {
        this._uid = ++_uidCounter;
        this._container = container;
        this._onChange = onChange;
        this._activePreset = (opciones && opciones.presetInicial) || '1M';
        this._showCustom = false;
        this._debounceTimer = null;
        this._sliderEl = null;

        // Fechas seleccionadas (Date objects, zona local)
        this._desde = null;
        this._hasta = null;

        // Rango total del slider (más amplio para contexto visual)
        this._rangoMinMs = getInicioDelAnio().getTime();
        this._rangoMaxMs = getFinDelDia().getTime();

        // Bound handlers
        this._boundContainerClick = this._handleContainerClick.bind(this);
        this._boundDateChange = this._handleDateChange.bind(this);

        // Inyectar estilos tema
        inyectarEstilosNoUiSlider();

        // Aplicar preset inicial sin emitir
        var preset = PRESETS_MAP[this._activePreset];
        if (preset) this._calcularFechasDesdePreset(preset);

        this._render();
    }

    // ========================================================================
    // API PÚBLICA
    // ========================================================================

    getActivePreset() { return this._activePreset; }

    getFechas() {
        return { desde: fmtISO(this._desde), hasta: fmtISO(this._hasta) };
    }

    setPreset(label) {
        var preset = PRESETS_MAP[label];
        if (preset) {
            this._calcularFechasDesdePreset(preset);
            this._render();
            this._emitir(preset.label);
        }
    }

    /**
     * Setea programáticamente un rango arbitrario y lo REFLEJA en la UI
     * (slider + label; ningún preset queda activo). Rango inválido = no-op.
     * @param {string} desdeStr — 'YYYY-MM-DD'
     * @param {string} hastaStr — 'YYYY-MM-DD'
     * @param {Object} [opciones]
     * @param {boolean} [opciones.emitir=true] — false para restaurar estado
     *   (p.ej. desde la URL) sin disparar onChange.
     */
    setRango(desdeStr, hastaStr, opciones) {
        if (!desdeStr || !hastaStr) return;
        var d = new Date(desdeStr + 'T00:00:00');
        var h = new Date(hastaStr + 'T23:59:59');
        if (isNaN(d.getTime()) || isNaN(h.getTime()) || d >= h) return;
        this._activePreset = null;
        this._desde = d;
        this._hasta = h;
        this._render();
        if (!opciones || opciones.emitir !== false) this._emitir(null);
    }

    destroy() {
        clearTimeout(this._debounceTimer);
        if (this._sliderEl && this._sliderEl.noUiSlider) {
            this._sliderEl.noUiSlider.destroy();
        }
        if (this._container) {
            this._container.removeEventListener('click', this._boundContainerClick);
            this._container.innerHTML = '';
        }
        this._container = null;
        this._onChange = null;
        this._sliderEl = null;
    }

    // ========================================================================
    // CORE
    // ========================================================================

    _emitir(preset) {
        // Todo preset (incluido AÑO) emite fechas explícitas. Nunca null: el
        // backend interpretaba desde/hasta ausentes como "solo hoy" (bug TUDO<1M).
        var dStr = this._desde ? fmtISO(this._desde) : null;
        var hStr = this._hasta ? fmtISO(this._hasta) : null;
        if (this._onChange) this._onChange(dStr, hStr, preset || null);
    }

    _calcularFechasDesdePreset(p) {
        this._activePreset = p.label;
        this._showCustom = false;
        var fin = getFinDelDia();

        if (p.dias === -1) {
            this._desde = getInicioDelAnio();
            this._hasta = fin;
        } else if (p.dias === 0) {
            this._desde = getInicioDelDia();
            this._hasta = fin;
        } else {
            this._hasta = fin;
            this._desde = new Date(fin.getTime());
            this._desde.setDate(this._desde.getDate() - p.dias);
            this._desde.setHours(0, 0, 0, 0);
        }
    }

    _aplicarCustom(dStr, hStr) {
        // Misma semántica que setear un rango externo: delega en la API pública.
        this.setRango(dStr, hStr);
    }

    // ========================================================================
    // RENDER
    // ========================================================================

    _render() {
        if (!this._container) return;
        var html = '';

        // ¿Rango corto? (≤3 días → mostrar horas)
        var rangoDias = this._desde && this._hasta
            ? (this._hasta.getTime() - this._desde.getTime()) / 86400000
            : 999;
        var esCorto = rangoDias <= 3;

        // Presets + período en la misma línea
        html += '<div class="tl-presets">';
        for (var i = 0; i < PRESETS.length; i++) {
            var active = this._activePreset === PRESETS[i].label ? ' active' : '';
            // El botón AÑO muestra el año en curso (2026), no un texto i18n fijo.
            var texto = PRESETS[i].dias === -1 ? String(new Date().getFullYear()) : t(PRESETS[i].label);
            html += '<button class="tl-btn' + active + '" data-preset="' + PRESETS[i].label + '">' + texto + '</button>';
        }
        html += '<button class="tl-btn tl-btn-rango' + (this._showCustom ? ' active' : '') + '" data-action="rango">' + t('Rango') + '</button>';
        html += '<span class="tl-tz" style="margin-left:auto">' + getTimezoneLabel() + '</span>';
        html += '</div>';

        // Custom date inputs
        if (this._showCustom) {
            html += '<div class="tl-custom">' +
                '<label class="tl-label">' + t('Desde') + '</label>' +
                '<input type="date" class="tl-date-input" data-field="desde" value="' + fmtISO(this._desde) + '">' +
                '<label class="tl-label">' + t('Hasta') + '</label>' +
                '<input type="date" class="tl-date-input" data-field="hasta" value="' + fmtISO(this._hasta) + '">' +
                '</div>';
        }

        // Slider container con fecha flotante centrada en el rango
        var dDisplay = esCorto ? fmtDisplay(this._desde) + ' ' + fmtHora(this._desde) : fmtDisplay(this._desde);
        var hDisplay = esCorto ? fmtDisplay(this._hasta) + ' ' + fmtHora(this._hasta) : fmtDisplay(this._hasta);
        html += '<div class="tl-slider-wrap" style="position:relative;margin:6px 0 4px">' +
            '<div class="tl-nouislider" id="tlSlider_' + this._uid + '"></div>' +
            '<span class="tl-period-label" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);z-index:5;font-size:11px;font-weight:700;color:#fff;background:rgba(30,60,100,0.85);padding:2px 10px;border-radius:4px;pointer-events:none;white-space:nowrap;border:1px solid rgba(59,130,246,0.35);text-shadow:0 1px 2px rgba(0,0,0,0.3)">' + dDisplay + ' — ' + hDisplay + '</span>' +
        '</div>';

        // Inject HTML
        this._container.removeEventListener('click', this._boundContainerClick);
        this._container.innerHTML = html;
        this._container.addEventListener('click', this._boundContainerClick);

        // Bind date inputs
        var dateInputs = this._container.querySelectorAll('.tl-date-input');
        for (var j = 0; j < dateInputs.length; j++) {
            dateInputs[j].addEventListener('change', this._boundDateChange);
        }

        // Montar noUiSlider
        this._montarSlider();
    }

    _montarSlider() {
        this._sliderEl = this._container.querySelector('#tlSlider_' + this._uid);
        if (!this._sliderEl || !this._desde || !this._hasta) return;

        var desdeMs = this._desde.getTime();
        var hastaMs = this._hasta.getTime();
        var self = this;

        // Rango del slider: margen de 10% a cada lado
        var rangoMs = hastaMs - desdeMs;
        var rangoDias = rangoMs / 86400000;
        // Step adaptativo: ≤3 días → 1 hora, >3 días → 1 día
        var stepMs = rangoDias <= 3 ? 3600000 : 86400000;
        var margen = Math.max(rangoMs * 0.10, stepMs);
        var sliderMin = desdeMs - margen;
        var sliderMax = hastaMs + margen;

        noUiSlider.create(this._sliderEl, {
            start: [desdeMs, hastaMs],
            connect: true,
            range: { min: sliderMin, max: sliderMax },
            step: stepMs,
            behaviour: 'drag-tap',
            tooltips: false,
        });

        // Al mover handles
        var esRangoCorto = rangoDias <= 3;
        this._sliderEl.noUiSlider.on('slide', function (values) {
            self._desde = new Date(parseInt(values[0]));
            self._hasta = new Date(parseInt(values[1]));
            if (!esRangoCorto) {
                self._desde.setHours(0, 0, 0, 0);
                self._hasta.setHours(23, 59, 59, 999);
            }
            self._activePreset = null;
            var dTxt = esRangoCorto ? fmtDisplay(self._desde) + ' ' + fmtHora(self._desde) : fmtDisplay(self._desde);
            var hTxt = esRangoCorto ? fmtDisplay(self._hasta) + ' ' + fmtHora(self._hasta) : fmtDisplay(self._hasta);
            var periodEl = self._container.querySelector('.tl-period-label');
            if (periodEl) {
                periodEl.textContent = dTxt + ' — ' + hTxt;
                self._posicionarLabelEnRango();
            }
            var presetEl = self._container.querySelector('.tl-preset-label');
            if (presetEl) presetEl.textContent = '';
            // Quitar active de presets
            var btns = self._container.querySelectorAll('.tl-btn[data-preset]');
            for (var b = 0; b < btns.length; b++) btns[b].classList.remove('active');
        });

        // Al soltar handle — emitir con debounce
        this._sliderEl.noUiSlider.on('change', function () {
            clearTimeout(self._debounceTimer);
            self._debounceTimer = setTimeout(function () {
                self._emitir(null);
            }, DEBOUNCE_MS);
        });

        // Posicionar label usando porcentajes del slider
        this._posicionarLabelEnRango();
    }

    /**
     * Posiciona el label de fechas centrado entre los dos handles.
     * Usa los porcentajes de noUiSlider (no getBoundingClientRect).
     * Centrado vertical siempre con CSS top:50% + transform.
     * Cuando el rango es muy estrecho, sube encima.
     */
    _posicionarLabelEnRango() {
        if (!this._sliderEl || !this._sliderEl.noUiSlider || !this._container) return;
        var label = this._container.querySelector('.tl-period-label');
        if (!label) return;

        // Obtener posiciones como porcentaje [0-100] desde noUiSlider
        var values = this._sliderEl.noUiSlider.get(true); // true = números, no strings
        var opts = this._sliderEl.noUiSlider.options;
        var min = opts.range.min;
        var max = opts.range.max;
        var totalRange = max - min;
        if (totalRange <= 0) return;

        var pctDesde = ((values[0] - min) / totalRange) * 100;
        var pctHasta = ((values[1] - min) / totalRange) * 100;
        var pctCenter = (pctDesde + pctHasta) / 2;
        var pctWidth = pctHasta - pctDesde;

        // Centrado horizontal entre handles (porcentaje)
        label.style.left = pctCenter + '%';

        // Centrado vertical siempre perfecto
        label.style.top = '50%';

        // Si el rango ocupa menos del 20% del slider, puede que el label no quepa
        var labelW = label.offsetWidth;
        var sliderW = this._sliderEl.offsetWidth || 1;
        var rangoPixels = (pctWidth / 100) * sliderW;

        if (rangoPixels < labelW + 16) {
            // No cabe dentro: poner encima
            label.style.top = '-6px';
            label.style.transform = 'translate(-50%, -100%)';
        } else {
            label.style.top = '50%';
            label.style.transform = 'translate(-50%, -50%)';
        }
    }

    // ========================================================================
    // EVENT HANDLERS
    // ========================================================================

    _handleContainerClick(e) {
        var btn = e.target.closest('.tl-btn');
        if (!btn) return;

        if (btn.dataset.action === 'rango') {
            this._showCustom = !this._showCustom;
            this._render();
            return;
        }

        var preset = PRESETS_MAP[btn.dataset.preset];
        if (preset) {
            this._calcularFechasDesdePreset(preset);
            this._render();
            this._emitir(preset.label);
        }
    }

    _handleDateChange() {
        var desdeInput = this._container.querySelector('.tl-date-input[data-field="desde"]');
        var hastaInput = this._container.querySelector('.tl-date-input[data-field="hasta"]');
        if (desdeInput && hastaInput) {
            this._aplicarCustom(desdeInput.value, hastaInput.value);
        }
    }
}

// ============================================================================
// API ESTÁTICA (compatibilidad)
// ============================================================================

var _singletonInstance = null;

var TimelineBar = {
    init: function (container, onChange) {
        if (_singletonInstance) _singletonInstance.destroy();
        _singletonInstance = new TimelineBarInstance(container, onChange);
    },
    destroy: function () {
        if (_singletonInstance) { _singletonInstance.destroy(); _singletonInstance = null; }
    },
    getActivePreset: function () {
        return _singletonInstance ? _singletonInstance.getActivePreset() : null;
    },
    getFechas: function () {
        return _singletonInstance ? _singletonInstance.getFechas() : { desde: null, hasta: null };
    },
};

export { TimelineBar, TimelineBarInstance };
export default TimelineBar;
